import { canOpenPortalJob } from '../../utils/portalJobNavigation';
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Spinner, Alert, Collapse, Form, Col, Row } from "react-bootstrap";
import { selectUserDetails, selectMappedRoles } from "../../redux/slices/userSlice";
import Tbd from "../JobDetailPage/Additional/Tbd";
import { setJobId } from "../../redux/slices/jobIdSlice";
import { useNavigate } from "react-router-dom";
import { getProjectId, getProjectStatus, idsMatch } from "../../utils/dataNormalization";
import { usePermissions } from "../../hooks/usePermissions";

function AssignedJobsWithTBD({ 
  jobs = [], 
  tbdList = [], 
  loading = false, 
  error = null 
}) {
  const userDetails = useSelector(selectUserDetails);
  const currentUser = userDetails?.name;
  const roles = useSelector(selectMappedRoles) || {};
  const { canView } = usePermissions();
  const showProjectManagerColumn = canView('user.assigned-jobs.project-manager-column');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Local state for UI functionality (not for API data)
  const [expandedRows, setExpandedRows] = useState([]);
  const [filters, setFilters] = useState({
    hideShippedJobs: true,
    onlyJobsWithTbd: false,
  });
  const [sortConfig, setSortConfig] = useState({ key: 'RequestedDeliveryDate', direction: 'ascending' });
  const [searchTerm, setSearchTerm] = useState("");

  // Filter jobs to show only those assigned to current user
  const userJobs = jobs.filter(job => 
    [job.SalesRep, job.ProjectManager, job.MechanicalEngineer, job.ElectricalEngineer, job.ControlsEngineer].includes(currentUser)
  );

  const hasTbd = (jobId) => {
    return tbdList.some(
      (tbdItem) => idsMatch(tbdItem.ProjectID, jobId)
    );
  };

  const handleRowClick = (jobId) => {
    dispatch(setJobId(jobId)); // Set the jobId when a row is clicked
    const currentExpandedRows = expandedRows;
    const isRowCurrentlyExpanded = currentExpandedRows.includes(jobId);

    const newExpandedRows = isRowCurrentlyExpanded
      ? currentExpandedRows.filter(id => id !== jobId)
      : currentExpandedRows.concat(jobId);

    setExpandedRows(newExpandedRows);
  };

  const handleFilterChange = (e) => {
    const { name, checked } = e.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: checked,
    }));
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };
  const sortedJobs = [...userJobs].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'ascending' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'ascending' ? 1 : -1;
    }
    return 0;
  });

  const visibleColumnCount = 4 +
    (showProjectManagerColumn ? 1 : 0) +
    (!roles.Sales ? 1 : 0) +
    (!roles.MechanicalEngineer ? 1 : 0) +
    (!roles.ElectricalEngineer ? 1 : 0) +
    (!roles.ControlEngineer ? 1 : 0);

  const filteredJobs = sortedJobs.filter((job) => {
    const projectStatus = getProjectStatus(job);

    const hideShippedJobsFilter = filters.hideShippedJobs
      ? projectStatus !== "Shipped" && projectStatus !== "canceled"
      : true;

    const onlyJobsWithTbdFilter = filters.onlyJobsWithTbd
      ? hasTbd(getProjectId(job))
      : true;

    const searchFilter = searchTerm
      ? job.JobNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.ProjectName?.toLowerCase().includes(searchTerm.toLowerCase())
      : true;

    return hideShippedJobsFilter && onlyJobsWithTbdFilter && searchFilter;
  });

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <div className="m-4">
      {error && <Alert variant="danger">{error}</Alert>}
      <div className="mb-3 text-center"><h1>Assigned Jobs</h1></div>
      <Row className="mb-3">
        <Col lg="auto">
          <Form.Control
            type="text"
            placeholder="Search by Job Number or Project Name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Col>
        <Col md="auto">
          <Form.Check
            type="checkbox"
            label="Hide Shipped/canceled Jobs"
            name="hideShippedJobs"
            checked={filters.hideShippedJobs}
            onChange={handleFilterChange}
          />
        </Col>
        <Col md="auto">
          <Form.Check
            type="checkbox"
            label="Only Jobs with TBDs"
            name="onlyJobsWithTbd"
            checked={filters.onlyJobsWithTbd}
            onChange={handleFilterChange}
          />
        </Col>
      </Row>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th onClick={() => handleSort('JobNumber')}>Job Number</th>
            <th onClick={() => handleSort('ProjectName')}>Project Name</th>
            {showProjectManagerColumn && <th onClick={() => handleSort('ProjectManager')}>Project Manager</th>}
            {!roles.Sales && <th onClick={() => handleSort('SalesRep')}>Sales Rep</th>}
            {!roles.MechanicalEngineer && <th onClick={() => handleSort('MechanicalEngineer')}>Mechanical Engineer</th>}
            {!roles.ElectricalEngineer && <th onClick={() => handleSort('ElectricalEngineer')}>Electrical Engineer</th>}
            {!roles.ControlEngineer && <th onClick={() => handleSort('ControlsEngineer')}>Control Engineer</th>}
            <th onClick={() => handleSort('ProjectStatus')}>Project Status</th>
            <th onClick={() => handleSort('RequestedDeliveryDate')}>Requested Delivery Date</th>
          </tr>
        </thead>
        <tbody>
          {filteredJobs.map((job) => {
            const projectId = getProjectId(job);
            // Check if navigation should be disabled
            const shouldDisableNavigation = 
              !canOpenPortalJob(job) ||
              job.BookingLogOnly === true || job.BookingLogOnly === "Yes";
            
            return (
              <React.Fragment key={projectId || job.ID}>
                <tr onClick={() => handleRowClick(projectId)}>
                  <td 
                    onClick={shouldDisableNavigation ? undefined : () => navigate(`/${projectId}/generalInfo`)}
                    style={{ 
                      cursor: shouldDisableNavigation ? 'default' : 'pointer',
                      color: shouldDisableNavigation ? '#6c757d' : 'inherit'
                    }}
                  >
                    {job.JobNumber}
                    {hasTbd(projectId) && (
                      <i
                        className="bi bi-exclamation-lg"
                        style={{ color: "red" }}
                        title="This job has TBDs"
                      ></i>
                    )}
                  </td>
                <td>{job.ProjectName}</td>
                {showProjectManagerColumn && <td>{job.ProjectManager}</td>}
                {!roles.Sales && <td>{job.SalesRep}</td>}
                {!roles.MechanicalEngineer && <td>{job.MechanicalEngineer}</td>}
                {!roles.ElectricalEngineer && <td>{job.ElectricalEngineer}</td>}
                {!roles.ControlEngineer && <td>{job.ControlsEngineer}</td>}
                <td>{getProjectStatus(job)}</td>
                <td>{formatDate(job.RequestedDeliveryDate)}</td>
              </tr>
              <tr>
                <td colSpan={visibleColumnCount}>
                  <Collapse in={expandedRows.includes(projectId)}>
                    <div>
                      {hasTbd(projectId) ? (
                        <Tbd jobId={projectId} />
                      ) : (
                        <p>No TBDs for this job</p>
                      )}
                    </div>
                  </Collapse>
                </td>
              </tr>
            </React.Fragment>
            );
          })}
        </tbody>
      </Table>
      {loading && <Spinner animation="border" />}
    </div>
  );
}

export default AssignedJobsWithTBD;
